import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BillingAddressElement,
  PaymentElement,
  TaxIdElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout'
import type { Appearance } from '@stripe/stripe-js'
import { AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import {
  CHECKOUT_BILLING_ADDRESS_OPTIONS,
  CHECKOUT_PAYMENT_ELEMENT_OPTIONS,
} from './checkout-defaults'
import { mapLiveCheckoutTotals, type LiveCheckoutTotals } from './order-summary-model'

interface CheckoutPaymentFormProps {
  appearance: Appearance
  // The submit label differs for a trial-bearing checkout.
  trialAvailable?: boolean
  // Fired when the payment succeeded in page. Presentation only - the webhook
  // remains the sole authority on billing access state.
  onComplete: () => void
  // Fired when the session can no longer be used and a new one is needed.
  onSessionExpired: () => void
  onTotalsChange?: (totals: LiveCheckoutTotals | null) => void
}

function FormShell({ children }: { children: React.ReactNode }) {
  return <div className="space-y-5">{children}</div>
}

function FormError({ message }: { message: string }) {
  return (
    <div role="alert" className="flex items-start gap-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  )
}

function incompleteCheckoutHint(trialAvailable: boolean): string {
  if (trialAvailable) {
    return 'Add your payment method and billing address to start the trial.'
  }
  return 'Add your payment method and billing address to subscribe.'
}

/**
 * The custom payment step: Stripe Elements bound to the Checkout Session,
 * styled with the app's own tokens instead of Stripe's iframe chrome. Must be
 * rendered inside a `CheckoutProvider`.
 */
export function CheckoutPaymentForm({
  appearance,
  trialAvailable = false,
  onComplete,
  onSessionExpired,
  onTotalsChange,
}: CheckoutPaymentFormProps) {
  const checkoutResult = useCheckout()
  const checkout = checkoutResult.type === 'success' ? checkoutResult.checkout : null

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [emailDraft, setEmailDraft] = useState('')
  const completedRef = useRef(false)
  const onSessionExpiredRef = useRef(onSessionExpired)
  const onTotalsChangeRef = useRef(onTotalsChange)
  onSessionExpiredRef.current = onSessionExpired
  onTotalsChangeRef.current = onTotalsChange

  const changeAppearance = checkout?.changeAppearance
  useEffect(() => {
    // Restyle in place on a theme switch; the provider cannot be remounted
    // without discarding the session's client secret.
    changeAppearance?.(appearance)
  }, [appearance, changeAppearance])

  const expired = checkout?.status.type === 'expired'
  useEffect(() => {
    if (expired) onSessionExpiredRef.current()
  }, [expired])

  const liveTotals = checkout ? mapLiveCheckoutTotals(checkout) : null
  const totalsSignature = liveTotals ? JSON.stringify(liveTotals) : null
  useEffect(() => {
    onTotalsChangeRef.current?.(totalsSignature ? (JSON.parse(totalsSignature) as LiveCheckoutTotals) : null)
  }, [totalsSignature])

  useEffect(
    () => () => {
      onTotalsChangeRef.current?.(null)
    },
    []
  )

  const updateEmail = checkout?.updateEmail
  const handleEmailBlur = useCallback(async () => {
    if (!updateEmail || !emailDraft.trim()) return
    const result = await updateEmail(emailDraft.trim())
    if (result.type === 'error') setSubmitError(result.error.message)
  }, [emailDraft, updateEmail])

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (!checkout || submitting) return
      if (!checkout.canConfirm) {
        setSubmitError(incompleteCheckoutHint(trialAvailable))
        return
      }

      setSubmitting(true)
      setSubmitError(null)
      try {
        // No returnUrl: the session's own return_url carries Stripe's
        // {CHECKOUT_SESSION_ID} placeholder, which a client-supplied URL
        // would replace and break the redirect-fallback landing.
        const result = await checkout.confirm({ redirect: 'if_required' })
        if (result.type === 'error') {
          setSubmitError(result.error.message)
          return
        }
        if (completedRef.current) return
        completedRef.current = true
        onComplete()
      } catch (error) {
        // Stripe throws IntegrationError for some confirm() conflicts instead
        // of returning { type: 'error' }. Swallowing that looks like a dead button.
        setSubmitError(error instanceof Error ? error.message : 'Unable to confirm checkout.')
      } finally {
        setSubmitting(false)
      }
    },
    [checkout, onComplete, submitting, trialAvailable]
  )

  if (checkoutResult.type === 'loading') {
    return (
      <FormShell>
        <div role="status" aria-label="Preparing checkout" className="space-y-4">
          <SkeletonBar className="h-10 w-1/3" />
          <SkeletonBar className="h-24 w-full" />
          <SkeletonBar className="h-40 w-full" />
          <SkeletonBar className="h-12 w-full" />
        </div>
      </FormShell>
    )
  }

  if (checkoutResult.type === 'error') {
    return (
      <FormShell>
        <FormError message={checkoutResult.error.message} />
        <Button type="button" onClick={onSessionExpired}>
          Try again
        </Button>
      </FormShell>
    )
  }

  if (!checkout || expired) {
    return (
      <FormShell>
        <FormError message="This checkout session expired. Start a new one to continue." />
        <Button type="button" onClick={onSessionExpired}>
          Start over
        </Button>
      </FormShell>
    )
  }

  const discounts = checkout.discountAmounts ?? []
  const lastPaymentError = checkout.lastPaymentError?.message

  return (
    <form onSubmit={handleSubmit} aria-busy={submitting} className="space-y-5">
      {checkout.email ? (
        <p className="text-sm text-muted-foreground">Billing to {checkout.email}</p>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="checkout-email">Email</Label>
          <Input
            id="checkout-email"
            type="email"
            autoComplete="email"
            value={emailDraft}
            onChange={(event) => setEmailDraft(event.target.value)}
            onBlur={handleEmailBlur}
          />
        </div>
      )}

      {/* The accordion layout degrades better than tabs at the compact
          density used by onboarding and billing settings. */}
      <PaymentElement options={CHECKOUT_PAYMENT_ELEMENT_OPTIONS} />
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium">Legal business name and billing address</p>
          <p className="text-xs text-muted-foreground">
            This business name appears on your Stripe invoices and receipts.
          </p>
        </div>
        <BillingAddressElement options={CHECKOUT_BILLING_ADDRESS_OPTIONS} />
      </div>
      <TaxIdElement options={{ visibility: 'auto', fields: { businessName: 'auto' } }} />

      {discounts.length > 0 ? (
        <dl className="space-y-1 border-t pt-3 text-sm">
          {discounts.map((discount) => (
            <div key={discount.displayName} className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">{discount.displayName}</dt>
              <dd className="font-medium">-{discount.amount}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {lastPaymentError && !submitError ? <FormError message={lastPaymentError} /> : null}
      {submitError ? <FormError message={submitError} /> : null}
      {!checkout.canConfirm && !submitError ? (
        <p id="checkout-submit-hint" className="text-sm text-muted-foreground">
          {incompleteCheckoutHint(trialAvailable)}
        </p>
      ) : null}

      <Button
        type="submit"
        className="w-full"
        disabled={submitting}
        aria-describedby={!checkout.canConfirm ? 'checkout-submit-hint' : undefined}
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {trialAvailable ? 'Start free trial' : 'Subscribe'}
      </Button>
    </form>
  )
}
